import { useCallback, useMemo, useState } from 'react'

import { Grid, Switch } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import Tag from 'src/components/DataDisplay/Tag/Tag'
import DateRangeMenu from 'src/components/Overlays/DateRangeMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICANT_CALL_CONVERSION_BY_STATUS,
  GET_BOOKING_OUTCOMES,
  GET_COMMON_DISCUSSION_TOPICS,
} from 'src/graphql/callAnaltyics.graphql'
import { GET_JOB_LISTINGS_ONLY } from 'src/graphql/joblistings.graphql'
import { GET_VOICE_AGENTS } from 'src/graphql/voiceAgents.graphql'

import FilterMenu from '../../../components/Overlays/FiltersMenu/FilterMenu'
import AnalyticsCard from '../../AnalyticsPage/components/AnalyticsCard'
import BookingOutcomeChart, {
  BOOKING_OUTCOME_INFO_TEXT,
} from '../components/BookingOutcomeChart'
import { EMPTY_BOOKING_OUTCOME_DATA } from '../components/bookingOutcomeChartTheme'
import BookingOutcomesByEmployeeTable, {
  BOOKINGS_BY_RECRUITER_INFO_TEXT,
} from '../components/BookingOutcomesByEmployeeTable'
import ConversionFunnel, {
  CONVERSION_FUNNEL_INFO_TEXT,
} from '../components/ConversionFunnel'
import ConversionPieChart, {
  CONVERSION_PIE_CHART_INFO_TEXT,
} from '../components/ConversionPieChart'
import DiscussionTopicsBarChart from '../components/DiscussionTopicsBarChart'
import { FiltersPortal } from '../components/FiltersPortal'
import {
  applicantConversionConnectedStatusOptions,
  leadSourceOptions,
  serializeDateRangeForUrl,
  updateUrl,
} from '../utils'
import { getUrlParams } from '../utils'

const VoiceAgentTab = () => {
  const urlParams = getUrlParams()
  const [selectedJobListings, setSelectedJobListings] = useState(
    urlParams.defaultJobListings
  )
  const [selectedLeadSources, setSelectedLeadSources] = useState(
    urlParams.defaultLeadSources
  )
  const [selectedConnectedStatus, setSelectedConnectedStatus] = useState(
    urlParams.defaultConnectedStatus
  )
  const [selectedVoiceAgents, setSelectedVoiceAgents] = useState(
    urlParams.defaultVoiceAgents
  )
  const [startDate, setStartDate] = useState(urlParams.defaultStartDate)
  const [endDate, setEndDate] = useState(urlParams.defaultEndDate)
  const [showTransferred, setShowTransferred] = useState(false)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const callLogFilters = useMemo(
    () => ({
      dateRange: { gte: startDate, lte: endDate },
      leadSources: selectedLeadSources,
      connectedStatuses: selectedConnectedStatus,
      jobListings: selectedJobListings,
      voiceAgents: selectedVoiceAgents,
    }),
    [
      startDate,
      endDate,
      selectedLeadSources,
      selectedConnectedStatus,
      selectedJobListings,
      selectedVoiceAgents,
    ]
  )

  const bookingOutcomeFilters = useMemo(
    () => ({
      dateRange: { gte: startDate, lte: endDate },
      voiceAgents: selectedVoiceAgents,
    }),
    [startDate, endDate, selectedVoiceAgents]
  )

  const { data: jobListingsResponse } = useQuery(GET_JOB_LISTINGS_ONLY, {
    variables: {
      pageInfo: { offset: 0, limit: 100 },
    },
    onError,
  })

  const jobListingOptions =
    jobListingsResponse?.jobListings.items?.map((jl) => ({
      value: jl.jobListingId,
      label: jl.title,
    })) || []

  const { data: voiceAgentsData } = useQuery(GET_VOICE_AGENTS, {
    variables: {
      pageInfo: { limit: 20, offset: 0 },
    },
    onError,
  })

  const voiceAgentOptions =
    voiceAgentsData?.voiceAgents?.items?.map((agent) => ({
      label: agent.name,
      value: agent.voiceAgentId,
    })) || []

  const { data: callConversionData, loading } = useQuery(
    GET_APPLICANT_CALL_CONVERSION_BY_STATUS,
    {
      variables: { filters: callLogFilters },
      onError,
    }
  )

  const { data: discussionTopicsData, loading: discussionTopicsLoading } =
    useQuery(GET_COMMON_DISCUSSION_TOPICS, {
      variables: { filters: callLogFilters },
      onError,
    })

  const { data: bookingOutcomesData, loading: bookingOutcomesLoading } =
    useQuery(GET_BOOKING_OUTCOMES, {
      variables: {
        filters: bookingOutcomeFilters,
        groupByRecruiter: false,
      },
      onError,
    })

  const discussionData = discussionTopicsData?.getCommonDiscussionTopics || []
  const isEmpty = discussionData.length === 0

  const bookingOutcomeTotals =
    bookingOutcomesData?.getBookingOutcomes?.[0] ?? EMPTY_BOOKING_OUTCOME_DATA

  return (
    <>
      <FiltersPortal>
        <div className="flex flex-row items-center gap-2">
          <FilterMenu
            filters={[
              {
                title: 'Lead Source',
                selected: selectedLeadSources,
                onChange: (value) => {
                  setSelectedLeadSources(value)
                  updateUrl('leadSources', value.join(','))
                },
                options: [...leadSourceOptions],
              },
              {
                title: 'Connected Status',
                selected: selectedConnectedStatus,
                onChange: (value) => {
                  setSelectedConnectedStatus(value)
                  updateUrl('connectedStatus', value.join(','))
                },
                options: [...applicantConversionConnectedStatusOptions],
              },
              {
                title: 'Job Listings',
                selected: selectedJobListings,
                onChange: (value) => {
                  setSelectedJobListings(value)
                  updateUrl('jobListings', value.join(','))
                },
                options: jobListingOptions ? [...jobListingOptions] : [],
              },
              {
                title: 'Voice Agent',
                selected: selectedVoiceAgents,
                onChange: (value) => {
                  setSelectedVoiceAgents(value)
                  updateUrl('voiceAgents', value.join(','))
                },
                options: voiceAgentOptions,
              },
            ]}
          />
          <DateRangeMenu
            startDate={startDate}
            endDate={endDate}
            handleChange={(value) => {
              setStartDate(value[0])
              setEndDate(value[1])
              updateUrl(
                'dateRange',
                serializeDateRangeForUrl(value[0], value[1])
              )
            }}
            clearable={false}
          />
        </div>
      </FiltersPortal>
      <Grid gutter="xs" className="h-full overflow-y-auto overflow-x-hidden">
        <Grid.Col span={5} className="flex flex-col gap-4">
          <AnalyticsCard
            title="Voice Agent Conversion Funnel"
            loading={loading}
            description={'Total calls to booked calls'}
            info={CONVERSION_FUNNEL_INFO_TEXT}
            headerChildren={
              <Switch
                size="xs"
                className="mt-2"
                label="Show Transferred"
                labelPosition="left"
                checked={showTransferred}
                onChange={() => setShowTransferred((prev) => !prev)}
              />
            }
          >
            <ConversionFunnel
              showTransferred={showTransferred}
              data={
                callConversionData?.getApplicantCallConversionByStatus || {
                  total: 0,
                  statuses: [],
                }
              }
            />
          </AnalyticsCard>
          <AnalyticsCard
            title="Voice Agent Call Statuses"
            loading={loading}
            description={'Statuses of calls across all contact attempts'}
            info={CONVERSION_PIE_CHART_INFO_TEXT}
          >
            <ConversionPieChart
              data={
                callConversionData?.getApplicantCallConversionByStatus || {
                  total: 0,
                  statuses: [],
                }
              }
            />
          </AnalyticsCard>
          <AnalyticsCard
            title="Voice Agent Discussion Topics"
            showBadge={false}
            loading={discussionTopicsLoading}
            description={
              'Top 10 most common discussion topics across all calls'
            }
            headerChildren={<Tag showCloseButton={false}>All Lead Sources</Tag>}
          >
            <DiscussionTopicsBarChart data={discussionData} isEmpty={isEmpty} />
          </AnalyticsCard>
        </Grid.Col>
        <Grid.Col span={7} className="flex flex-col gap-4">
          <AnalyticsCard
            title="Booking Outcomes"
            loading={bookingOutcomesLoading}
            showBadge={false}
            description="Split of bookings by outcome — completed, no-show, and cancelled"
            info={BOOKING_OUTCOME_INFO_TEXT}
          >
            <BookingOutcomeChart data={bookingOutcomeTotals} />
          </AnalyticsCard>
          <AnalyticsCard
            title="Bookings by Recruiter"
            showBadge={false}
            description="Per-recruiter booking volume and outcome split for the selected period"
            info={BOOKINGS_BY_RECRUITER_INFO_TEXT}
          >
            <BookingOutcomesByEmployeeTable filters={bookingOutcomeFilters} />
          </AnalyticsCard>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default VoiceAgentTab
